import React, { useContext, useMemo, useCallback, useState, useEffect, memo } from 'react';
import { Handle, Position, NodeToolbar, useUpdateNodeInternals } from 'reactflow';
import styles from './styles/SituationNode.module.css';
import AuthContext from '../../../context/AuthContext';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import { hashStringToColor } from '../../../utils/collabColors';
import { nodeKeyRoles } from './keys/keyGraph';
import { KeyRoleIcon } from './keys/KeyBadge';
import { useKeyHighlight } from './keys/KeyHighlightContext';
import { FaKey, FaSyncAlt, FaTimes } from 'react-icons/fa';

const SIDES = [
    { pos: Position.Top, label: 'Top' },
    { pos: Position.Right, label: 'Right' },
    { pos: Position.Bottom, label: 'Bottom' },
    { pos: Position.Left, label: 'Left' },
];

/**
 * A miniature of the node with a button on each side. The side holding the
 * handle is filled in that handle's own colour, so the picker reads as a map of
 * the real node rather than a row of abbreviations.
 */
const HandleSidePicker = ({ label, role, value, onChange }) => {
    const current = SIDES.find((side) => side.pos === value);

    return (
        <div className={`${styles.picker} ${role === 'source' ? styles.pickerSource : styles.pickerTarget}`}>
            <div className={styles.pickerLabel}>
                <span className={styles.pickerDot} />
                {label}
            </div>
            <div className={styles.pickerBox}>
                {SIDES.map(({ pos, label: sideLabel }) => (
                    <button
                        key={pos}
                        type="button"
                        className={`${styles.pickerSide} ${styles[`pickerSide_${pos}`]} ${
                            value === pos ? styles.pickerSideActive : ''
                        }`}
                        title={`${label} on the ${sideLabel.toLowerCase()}`}
                        aria-label={`${label} on the ${sideLabel.toLowerCase()}`}
                        aria-pressed={value === pos}
                        onClick={() => onChange(pos)}
                    />
                ))}
            </div>
            <div className={styles.pickerCaption}>{current?.label}</div>
        </div>
    );
};

const SituationNode = ({
    id,
    data,
    isConnectable,
    dragging,
    showHandles = true,
    edgeConnecting,
    selectedUsers
}) => {
    const { userId: authUserId } = useContext(AuthContext);
    const { updateSituation, getGroup } = useCanvas();
    // The editor's own selection, not ReactFlow's: ReactFlow marks a node
    // selected the moment it is dragged, which is not the author picking it.
    const { selectedNodeId } = useEditorUI();
    const isSelected = selectedNodeId === id;

    // The handle editor stays out of the way until asked for, and closes again
    // whenever the node is deselected.
    const [editingHandles, setEditingHandles] = useState(false);
    useEffect(() => {
        if (!isSelected) setEditingHandles(false);
    }, [isSelected]);

    const sourcePos = data.sourcePosition || Position.Right;
    const targetPos = data.targetPosition || Position.Left;

    const updateNodeInternals = useUpdateNodeInternals();

    const changeHandlePos = useCallback((handleType, newPosition) => {
        if (data[handleType] === newPosition) return;

        updateSituation(id, { [handleType]: newPosition });

        setTimeout(() => {
            updateNodeInternals(id);
        }, 10);
    },[id, data, updateSituation, updateNodeInternals]);

    const highlightColor = useMemo(() => {
        if (!selectedUsers || selectedUsers.length === 0) return undefined;
        
        if (selectedUsers.includes(authUserId)) {
            return hashStringToColor(authUserId);
        }
        return hashStringToColor(selectedUsers[0]);
    }, [selectedUsers, authUserId]);

    const { grants: grantsKey, removes: removesKey } = nodeKeyRoles(data);
    const keyHighlight = useKeyHighlight();
    const isDimmed = !!keyHighlight && !keyHighlight.nodeIds.has(String(id));

    const nodeClassName = useMemo(() => {
        let cls = styles.nodeContainer;
        if (highlightColor) cls += ` ${styles.selected}`;
        if (edgeConnecting) cls += ` ${styles.edgeConnecting}`;
        if (isDimmed) cls += ` ${styles.dimmed}`;
        if (editingHandles) cls += ` ${styles.editingHandles}`;

        if (data.endpointType === 'start') cls += ` ${styles.startNode}`;
        else if (data.endpointType === 'end') cls += ` ${styles.endNode}`;
        else cls += ` ${styles.regularNode}`;

        return cls;
    },[highlightColor, edgeConnecting, isDimmed, editingHandles, data.endpointType]);

    // A grouped situation takes its colour FROM its group, resolved here at
    // render time. It used to be copied onto every member node whenever the
    // group's colour changed, which meant two writes and two chances to drift.
    const fillColor = useMemo(() => {
        const group = data.situationGroup ? getGroup?.(data.situationGroup) : null;
        return group?.color || data.fillColor || '#fff';
    }, [data.situationGroup, data.fillColor, getGroup]);

    const nodeStyle = useMemo(() => ({
        '--fill-color': fillColor,
        '--border-color': data.borderColor || '#ddd',
        '--highlight-color': highlightColor,
    }),[fillColor, data.borderColor, highlightColor]);

    const stop = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
    },[]);

    const isStart = data.endpointType === 'start';
    const isEnd = data.endpointType === 'end';

    return (
        <div className={nodeClassName} style={nodeStyle}>
            {/* Below the node, because a self-loop hint is drawn above it. */}
            <NodeToolbar
                isVisible={isSelected && editingHandles && !dragging}
                position={Position.Bottom}
                offset={14}
            >
                {/* The toolbar is portalled, but React events still bubble to the
                    node, where a click would toggle the selection off. */}
                <div className={styles.toolbarContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.panelHeader}>
                        <span>Connection points</span>
                        <button
                            type="button"
                            className={styles.panelClose}
                            aria-label="Close"
                            onClick={() => setEditingHandles(false)}
                        >
                            <FaTimes size={10} />
                        </button>
                    </div>

                    <div className={styles.pickerRow}>
                        <HandleSidePicker
                            label="Incoming"
                            role="target"
                            value={targetPos}
                            onChange={(pos) => changeHandlePos('targetPosition', pos)}
                        />
                        {!isEnd && (
                            <HandleSidePicker
                                label="Outgoing"
                                role="source"
                                value={sourcePos}
                                onChange={(pos) => changeHandlePos('sourcePosition', pos)}
                            />
                        )}
                    </div>

                    <div className={styles.panelHint}>
                        {isEnd
                            ? 'Choices arrive at Incoming.'
                            : 'Choices arrive at Incoming and leave from Outgoing.'}
                    </div>
                </div>
            </NodeToolbar>

            {isSelected && !dragging && (
                <button
                    type="button"
                    className={`nodrag ${styles.handleToggle} ${
                        editingHandles ? styles.handleToggleActive : ''
                    }`}
                    title="Move connection points"
                    aria-label="Move connection points"
                    aria-pressed={editingHandles}
                    onMouseDown={stop}
                    onClick={(e) => {
                        stop(e);
                        setEditingHandles((open) => !open);
                    }}
                >
                    <FaSyncAlt size={9} />
                </button>
            )}

            {isStart && <div className={styles.nodeLabel}>START</div>}
            {isEnd && <div className={styles.nodeLabel}>END</div>}

            {grantsKey && (
                <span
                    className={styles.keyBadge}
                    title="Players pick up a key here"
                    aria-label="Players pick up a key here"
                >
                    <FaKey size={9} />
                </span>
            )}
            {removesKey && (
                <span
                    className={`${styles.keyBadge} ${styles.keyBadgeRemove} ${grantsKey ? styles.keyBadgeSecond : ''}`}
                    title="Players lose a key here"
                    aria-label="Players lose a key here"
                >
                    <KeyRoleIcon role="remove" size={9} color="#fff" />
                </span>
            )}

            <div className={styles.nodeContent}>
                
                {showHandles && (
                    <Handle
                        type="target"
                        position={targetPos}
                        className={isEnd ? `${styles.handle} ${styles.handleTarget}` : styles.handleTarget}
                        isConnectable={isConnectable}
                        onMouseDown={stop}
                        onClick={stop}
                    />
                )}
                
                {data.title || 'Untitled'}

                {showHandles && !isEnd && (
                    <Handle
                        type="source"
                        position={sourcePos}
                        id={isStart ? "a" : "b"} 
                        className={styles.handleSource}
                        isConnectable={isConnectable}
                        onMouseDown={stop}
                        onClick={stop}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(SituationNode);